import { Button, TextArea, TextField } from '@scale/scaleui';
import { ReferenceText } from '@scale/shared/types/chat';
import { useCallback, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import cn from 'classnames';
import WordCounter from '../../common/WordCounter';
import { StepCard } from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/StepCard';
import { twMerge } from 'tailwind-merge';
import Draggable from 'react-draggable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
type EditReferenceTextModalProps = {
  label?: string;
  isOpen: boolean;
  onClose: () => void;
  referenceText: ReferenceText;
  setReferenceText: (referenceText: ReferenceText) => void;
  readOnly?: boolean;
  hideUrl?: boolean;
  showReferenceCategory?: boolean;
  required?: boolean;
  setIsOpen: (isOpen: boolean) => void;
  minReferenceTextsLength?: number;
  maxReferenceTextsLength?: number;
};

const EditReferenceTextModal = ({
  label,
  isOpen,
  onClose,
  setReferenceText,
  referenceText,
  required = false,
  hideUrl = false,
  showReferenceCategory = false,
  readOnly,
  setIsOpen,
  minReferenceTextsLength,
  maxReferenceTextsLength,
}: EditReferenceTextModalProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      referenceTextContent: referenceText?.content,
      referenceTextUrl: referenceText?.url,
      referenceTextCategory: referenceText?.category,
    },
  });

  // TODO: @chat-task-eng readd reset here when fixed https://github.com/scaleapi/scaleapi/pull/71364
  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setReferenceText &&
        handleSubmit((data: any) => {
          setReferenceText({
            content: data.referenceTextContent,
            url: data.referenceTextUrl,
            category: data.referenceTextCategory,
          });
          onClose();
        })();
    },
    [setReferenceText, handleSubmit, onClose],
  );

  const [isDragging, setIsDragging] = useState(false);
  const isNewReferenceText = !label; // Not the most intuitive contract.

  if (!isOpen) {
    return <></>;
  }
  return (
    <StepCard.Popover
      placement="right-start"
      isOpen={isOpen}
      icon="paperclip"
      onClick={() => {
        setIsOpen(!isOpen);
      }}
      tooltip="Reference Text"
      disablePopoverButton
      useStepCardReference
    >
      <Draggable
        onStart={() => {
          setIsDragging(true);
        }}
        onStop={() => {
          setIsDragging(false);
        }}
        cancel=".non-draggable"
      >
        <div
          className={twMerge(
            'flex flex-col gap-4 min-w-[32rem] bg-neutral-0 p-4 rounded-lg border border-neutral-200 fixed',
            isDragging ? 'cursor-grabbing' : 'cursor-grab',
            'overscroll-none',
          )}
        >
          <form onSubmit={handleFormSubmit}>
            <FontAwesomeIcon
              icon={'close'}
              className="fixed top-0 right-0 m-4 cursor-pointer"
              onClick={() => setIsOpen(!isOpen)}
            />
            <Controller
              name="referenceTextContent"
              control={control}
              rules={{
                required: required ? '* This field is required' : undefined,
              }}
              render={({ field }) => (
                <div className="mb-2">
                  <TextArea
                    {...field}
                    id="referenceTextContent"
                    required={required}
                    error={!!errors.referenceTextContent?.message}
                    textAreaClassName="non-draggable"
                    className="mb-2"
                    rows={15}
                    description={errors.referenceTextContent?.message}
                    label={isNewReferenceText ? 'New Reference Text' : label}
                    disabled={readOnly}
                  />
                  <WordCounter text={field.value} />

                  {((field.value?.length ?? 0) < (minReferenceTextsLength ?? 0) ||
                    (field.value?.length ?? 0) > (maxReferenceTextsLength ?? Infinity)) && (
                    <div className="text-red-500">
                      Reference text must be between {minReferenceTextsLength} and{' '}
                      {maxReferenceTextsLength} characters long.
                    </div>
                  )}
                </div>
              )}
            />
            {!hideUrl && (
              <Controller
                name="referenceTextUrl"
                control={control}
                rules={{
                  required: required ? '* This field is required' : undefined,
                  pattern: {
                    value:
                      /https?:\/\/(www.)?[-a-zA-Z0-9@:%._+~#=]{1,256}.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/,
                    message: 'URL must start with http:// or https://',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    required={required}
                    id="referenceTextUrl"
                    inputClassName="non-draggable"
                    className={cn('mb-2')}
                    error={!!errors.referenceTextUrl?.message}
                    label="Reference Url"
                    description={errors.referenceTextUrl?.message}
                    disabled={readOnly}
                  />
                )}
              />
            )}
            {showReferenceCategory && (
              <Controller
                name="referenceTextCategory"
                control={control}
                rules={{
                  required: required ? '* This field is required' : undefined,
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    required={required}
                    id="referenceCategory"
                    inputClassName="non-draggable"
                    className={cn('mb-2')}
                    error={!!errors.referenceTextCategory?.message}
                    label="Reference Category"
                    description={errors.referenceTextCategory?.message}
                    disabled={readOnly}
                  />
                )}
              />
            )}
          </form>
          {!readOnly && (
            <div className="flex justify-end">
              <Button disabled={readOnly} onClick={handleFormSubmit}>
                {isNewReferenceText ? 'Add Reference Text' : 'Edit Reference Text'}
              </Button>
            </div>
          )}
        </div>
      </Draggable>
    </StepCard.Popover>
  );
};

export default EditReferenceTextModal;
